// ─── Memory Collage V1 ────────────────────────────────────────
// Canvas-based scrapbook wedding invitation. Free-positioned
// polaroids, handwritten notes, editorial text, and title blocks.
// Desktop/mobile responsive layouts per element.
// Each element carries an `intent` for smarter rendering.

export const memoryCollageV1 = {
  templateId: 'memory-collage-v1',
  name: 'Memory Collage',
  description: 'Scrapbook-style collage. Polaroids, handwritten notes, and layered memories.',
  category: 'wedding',
  previewColors: ['#f2ece6', '#9b7f6b', '#221c18'],
  isCollage: true,
  theme: {
    palette: 'warm-paper',
    fontPair: 'cormorant-manrope',
    radius: 'soft',
    shadow: 'soft',
    overlay: 'none',
    themeExtensions: {
      paper: '#fbf6f1',
      borderStrong: '#d8cbc0',
      monoFont: '"Courier Prime", "Courier New", monospace',
      handFont: '"Bradley Hand", "Comic Sans MS", cursive',
      shadowSoft: '0 16px 50px rgba(0,0,0,0.10)',
      shadowPaper: '0 10px 30px rgba(0,0,0,0.08)',
    },
  },
  settings: {
    musicEnabled: false,
    countdownEnabled: false,
    rsvpEnabled: false,
    showMapLink: false,
  },
  sections: [
    // ━━━ HERO ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
    {
      id: 'hero-collage',
      type: 'collage',
      label: 'Hero',
      variant: 'memory-hero',
      visible: true,
      height: { mobile: 920, desktop: 980 },
      background: {
        type: 'image',
        src: 'https://images.unsplash.com/photo-1507525428034-b723cf961d3e?q=80&w=1600&auto=format&fit=crop',
        overlay: 'rgba(34,28,24,0.20)',
        grayscale: 0.35,
      },
      editableRules: {
        fields: ['elements'],
        locked: ['background', 'height'],
      },
      data: {},
      elements: [
        {
          id: 'hero-polaroid',
          type: 'image_card',
          style: 'polaroid',
          src: 'https://images.unsplash.com/photo-1519741497674-611481863552?q=80&w=1200&auto=format&fit=crop',
          caption: 'the first day of forever',
          rotation: -2,
          intent: {
            role: 'image',
            importance: 'high',
            behavior: 'flexible',
            mobileBehavior: 'preserve',
            overlapPolicy: 'avoidText',
            animationPriority: 2,
          },
          layout: {
            desktop: { x: 0.5, y: 0.40, w: 0.40, z: 5 },
            mobile: { x: 0.5, y: 0.30, w: 0.72, z: 5 },
          },
        },
        {
          id: 'hero-note',
          type: 'text_note',
          style: 'handwritten-paper',
          content: 'one beautiful day.\none unforgettable night.',
          rotation: -4,
          intent: {
            role: 'decorative',
            importance: 'medium',
            behavior: 'flexible',
            mobileBehavior: 'preserve',
            overlapPolicy: 'allow',
            animationPriority: 4,
          },
          layout: {
            desktop: { x: 0.28, y: 0.68, w: 0.22, z: 6 },
            mobile: { x: 0.28, y: 0.58, w: 0.42, z: 6 },
          },
        },
        {
          id: 'hero-kicker-left',
          type: 'text_block',
          style: 'micro-label',
          content: 'poster design 2026',
          intent: {
            role: 'metaText',
            importance: 'low',
            behavior: 'decorative',
            mobileBehavior: 'hide',
            overlapPolicy: 'allow',
            animationPriority: 6,
          },
          layout: {
            desktop: { x: 0.10, y: 0.86, w: 0.18, z: 2 },
            mobile: { x: 0.12, y: 0.78, w: 0.28, z: 2 },
          },
        },
        {
          id: 'hero-kicker-center',
          type: 'text_block',
          style: 'micro-label-center',
          content: 'memory collage',
          intent: {
            role: 'metaText',
            importance: 'low',
            behavior: 'decorative',
            mobileBehavior: 'hide',
            overlapPolicy: 'allow',
            animationPriority: 6,
          },
          layout: {
            desktop: { x: 0.50, y: 0.87, w: 0.24, z: 2 },
            mobile: { x: 0.50, y: 0.78, w: 0.34, z: 2 },
          },
        },
        {
          id: 'hero-kicker-right',
          type: 'text_block',
          style: 'micro-label-right',
          content: 'embrace the summer',
          intent: {
            role: 'metaText',
            importance: 'low',
            behavior: 'decorative',
            mobileBehavior: 'hide',
            overlapPolicy: 'allow',
            animationPriority: 6,
          },
          layout: {
            desktop: { x: 0.90, y: 0.86, w: 0.18, z: 2 },
            mobile: { x: 0.88, y: 0.78, w: 0.28, z: 2 },
          },
        },
        {
          id: 'hero-title',
          type: 'title_block',
          style: 'hero-title',
          title: 'Ava & Luca',
          subtitle: 'Join us for a day filled with love, music, and the people who mean the most.',
          meta: '14 August 2026 \u00b7 Amalfi Coast',
          buttonText: 'See the story',
          buttonHref: '#story-collage',
          intent: {
            role: 'primaryHeadline',
            importance: 'high',
            behavior: 'fixed',
            mobileBehavior: 'preserve',
            overlapPolicy: 'none',
            animationPriority: 1,
          },
          layout: {
            desktop: { x: 0.50, y: 0.95, w: 0.60, z: 4 },
            mobile: { x: 0.50, y: 0.90, w: 0.84, z: 4 },
          },
        },
      ],
    },
    // ━━━ STORY ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
    {
      id: 'story-collage',
      type: 'collage',
      label: 'Story',
      variant: 'memory-story',
      visible: true,
      height: { mobile: 1380, desktop: 1180 },
      background: {
        type: 'color',
        color: '#fffdfb',
      },
      editableRules: {
        fields: ['elements'],
        locked: ['background', 'height'],
      },
      data: {},
      elements: [
        {
          id: 'story-image-1',
          type: 'image_card',
          style: 'plain-photo',
          src: 'https://images.unsplash.com/photo-1522673607200-164d1b6ce486?q=80&w=1200&auto=format&fit=crop',
          rotation: -1,
          intent: {
            role: 'image',
            importance: 'high',
            behavior: 'flexible',
            mobileBehavior: 'preserve',
            overlapPolicy: 'avoidText',
            animationPriority: 2,
          },
          layout: {
            desktop: { x: 0.22, y: 0.22, w: 0.26, z: 3 },
            mobile: { x: 0.28, y: 0.13, w: 0.60, z: 3 },
          },
        },
        {
          id: 'story-text-1',
          type: 'text_block',
          style: 'typewriter',
          content: "Let's celebrate.\n\nThe best things in life are meant to be shared. Thank you for being here and for filling this day with love, support, and meaning.",
          intent: {
            role: 'bodyText',
            importance: 'medium',
            behavior: 'flexible',
            mobileBehavior: 'preserve',
            overlapPolicy: 'avoidImportant',
            animationPriority: 3,
          },
          layout: {
            desktop: { x: 0.23, y: 0.47, w: 0.28, z: 4 },
            mobile: { x: 0.28, y: 0.36, w: 0.62, z: 4 },
          },
        },
        {
          id: 'story-text-2',
          type: 'text_block',
          style: 'typewriter-small-right',
          content: "Thank you so much for celebrating our special day.\nWe hope you had as much fun as we did.\n\nLove",
          intent: {
            role: 'bodyText',
            importance: 'medium',
            behavior: 'flexible',
            mobileBehavior: 'preserve',
            overlapPolicy: 'avoidImportant',
            animationPriority: 3,
          },
          layout: {
            desktop: { x: 0.73, y: 0.12, w: 0.24, z: 4 },
            mobile: { x: 0.71, y: 0.50, w: 0.46, z: 4 },
          },
        },
        {
          id: 'story-image-2',
          type: 'image_card',
          style: 'plain-photo-large',
          src: 'https://images.unsplash.com/photo-1511285560929-80b456fea0bc?q=80&w=1200&auto=format&fit=crop',
          rotation: 0,
          intent: {
            role: 'image',
            importance: 'high',
            behavior: 'flexible',
            mobileBehavior: 'preserve',
            overlapPolicy: 'avoidText',
            animationPriority: 2,
          },
          layout: {
            desktop: { x: 0.67, y: 0.43, w: 0.34, z: 3 },
            mobile: { x: 0.68, y: 0.60, w: 0.58, z: 3 },
          },
        },
        {
          id: 'story-note',
          type: 'text_note',
          style: 'torn-note',
          content: "i love you\ni will always love you,\neven when the world\ngets loud around us.",
          rotation: -6,
          intent: {
            role: 'decorative',
            importance: 'low',
            behavior: 'decorative',
            mobileBehavior: 'preserve',
            overlapPolicy: 'allow',
            animationPriority: 5,
          },
          layout: {
            desktop: { x: 0.24, y: 0.83, w: 0.26, z: 6 },
            mobile: { x: 0.29, y: 0.82, w: 0.60, z: 6 },
          },
        },
        {
          id: 'story-quote-cn',
          type: 'text_block',
          style: 'editorial-right',
          content: "\u4ece\u6b64\u6211\u4eec\u624b\u62c9\u624b\n\u5411\u7740\u540c\u4e00\u4e2a\u65b9\u5411\u8d70\n\n\u76f4\u5230\u5929\u9ed1\n\u5f85\u751f\u547d\u7ed3\u675f\n\u6211\u4eec\u624d\u7ed3\u675f",
          intent: {
            role: 'decorative',
            importance: 'low',
            behavior: 'decorative',
            mobileBehavior: 'hide',
            overlapPolicy: 'allow',
            animationPriority: 5,
          },
          layout: {
            desktop: { x: 0.79, y: 0.83, w: 0.20, z: 4 },
            mobile: { x: 0.76, y: 0.95, w: 0.34, z: 4 },
          },
        },
      ],
    },
    // ━━━ FOOTER ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
    {
      id: 'footer-collage',
      type: 'collage',
      label: 'Footer',
      variant: 'memory-footer',
      visible: true,
      height: { mobile: 420, desktop: 460 },
      background: {
        type: 'image',
        src: 'https://images.unsplash.com/photo-1500375592092-40eb2168fd21?q=80&w=1600&auto=format&fit=crop',
        overlay: 'rgba(255,250,246,0.34)',
        grayscale: 0.4,
      },
      editableRules: {
        fields: ['elements'],
        locked: ['background', 'height'],
      },
      data: {},
      elements: [
        {
          id: 'footer-message',
          type: 'title_block',
          style: 'footer-title',
          title: "We can't wait to celebrate with you.",
          subtitle: 'Ava & Luca',
          intent: {
            role: 'primaryHeadline',
            importance: 'high',
            behavior: 'fixed',
            mobileBehavior: 'preserve',
            overlapPolicy: 'none',
            animationPriority: 1,
          },
          layout: {
            desktop: { x: 0.50, y: 0.52, w: 0.60, z: 4 },
            mobile: { x: 0.50, y: 0.52, w: 0.84, z: 4 },
          },
        },
      ],
    },
  ],
};
